import { useOnAuthStateChanged } from '@/app/_auth/auth-hooks'
import { AuthGuard, AuthStep } from '@/app/_auth/auth-step-navigator'
import { AuthProviderID } from '@/app/_auth/steps/_common/login-buttons'
import type { EmergenceUser } from '@/hooks/api/use-emergence-user-and-step-query'
import { clearPersistentPagination } from '@/hooks/use-persistent-pagination'
import { clearPersistentSorting } from '@/hooks/use-persistent-sorting'
import type { FirebaseError } from 'firebase/app'
import React from 'react'
import {
  HISTORY_TABLE_PAGINATION_KEY,
  HISTORY_TABLE_SORTING_KEY,
} from '../chat-history/chat-history'

const AuthProvider = ({ children }: React.PropsWithChildren) => {
  const [step, setStep] = React.useState<AuthContextValue['step']>(AuthStep.GetSessionData)
  const [user, setUser] = React.useState<AuthContextValue['user']>(null)
  const [authError, setAuthError] = React.useState<AuthContextValue['authError']>(null)
  const [apiBaseUrl, setApiBaseUrl] = React.useState<AuthContextValue['apiBaseUrl']>(null)

  // Wrap setters with logging
  const setStepWithLogging = React.useCallback((newStep: React.SetStateAction<AuthStep | null>) => {
    setStep((prevStep) => {
      const resolvedStep = typeof newStep === 'function' ? newStep(prevStep) : newStep
      return resolvedStep
    })
  }, [])

  const setUserWithLogging = React.useCallback(
    (newUser: React.SetStateAction<EmergenceUser | null>) => {
      setUser((prevUser) => {
        const resolvedUser = typeof newUser === 'function' ? newUser(prevUser) : newUser
        return resolvedUser
      })
    },
    [],
  )

  const setAuthErrorWithLogging = React.useCallback(
    (newError: React.SetStateAction<AuthError | null>) => {
      setAuthError((prevError) => {
        const resolvedError = typeof newError === 'function' ? newError(prevError) : newError
        return resolvedError
      })
    },
    [],
  )

  const setApiBaseUrlWithLogging = React.useCallback(
    (newUrl: React.SetStateAction<string | null>) => {
      setApiBaseUrl((prevUrl) => {
        const resolvedUrl = typeof newUrl === 'function' ? newUrl(prevUrl) : newUrl
        return resolvedUrl
      })
    },
    [],
  )

  const value = React.useMemo<AuthContextValue>(
    () => ({
      step,
      setStep: setStepWithLogging,
      user,
      setUser: setUserWithLogging,
      authError,
      setAuthError: setAuthErrorWithLogging,
      apiBaseUrl,
      setApiBaseUrl: setApiBaseUrlWithLogging,
    }),
    [
      step,
      user,
      authError,
      apiBaseUrl,
      setStepWithLogging,
      setUserWithLogging,
      setAuthErrorWithLogging,
      setApiBaseUrlWithLogging,
    ],
  )

  useOnAuthStateChanged({
    onLogin: () => setStepWithLogging(AuthStep.GetSessionData),
    onLogout: () => {
      setUserWithLogging(null)
      setStepWithLogging(AuthStep.Login)
      clearPersistentPagination(HISTORY_TABLE_PAGINATION_KEY)
      clearPersistentSorting(HISTORY_TABLE_SORTING_KEY)
    },
  })

  return (
    <AuthContextProvider value={value}>
      <AuthGuard step={step}>{children}</AuthGuard>
    </AuthContextProvider>
  )
}

// 👇 auth context

const AuthContextProvider = React.memo(
  ({ children, value }: React.PropsWithChildren<{ value: AuthContextValue }>) => (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  ),
)

interface AuthError extends FirebaseError {
  customData?: FirebaseError['customData'] & {
    email?: string
    _tokenResponse?: { providerId?: AuthProviderID; verifiedProvider?: AuthProviderID[] }
  }
}

type AuthContextValue = {
  step: AuthStep | null
  setStep: SetState<AuthStep | null>
  user: EmergenceUser | null
  setUser: SetState<EmergenceUser | null>
  authError: AuthError | null
  setAuthError: SetState<AuthError | null>
  apiBaseUrl: string | null
  setApiBaseUrl: SetState<string | null>
}

const error = (name: string) => () => {
  throw new Error(`AuthContext.${name} used outside of AuthProvider`)
}

const AuthContext = React.createContext<AuthContextValue>({
  user: null,
  setUser: error('setUser'),
  step: AuthStep.GetSessionData,
  setStep: error('setStep'),
  authError: null,
  setAuthError: error('setAuthError'),
  apiBaseUrl: null,
  setApiBaseUrl: error('setApiBaseUrl'),
})

export { AuthContext, AuthContextProvider, AuthProvider, type AuthContextValue }
